import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/base/SEOHead';

export default function TaxRegistrationGuidePage() {
  const taxTypes = [
    {
      title: 'Employer Identification Number (EIN)',
      authority: 'Internal Revenue Service (IRS)',
      icon: 'ri-government-line',
      required: 'Required for businesses with employees, partnerships, corporations, and LLCs',
      cost: 'Free',
      howToApply: 'Apply online at IRS.gov/EIN - instant approval',
      deadline: 'Before hiring employees or opening business bank account',
      details: [
        'Used for federal tax filing and reporting',
        'Required to open business bank accounts',
        'Needed for hiring employees',
        'Can be obtained immediately online',
      ],
    },
    {
      title: 'Texas Sales Tax Permit',
      authority: 'Texas Comptroller of Public Accounts',
      icon: 'ri-shopping-cart-line',
      required: 'Required if selling tangible goods or taxable services',
      cost: 'Free',
      howToApply: 'Register online at Comptroller.Texas.gov',
      deadline: 'Before making first sale',
      details: [
        'El Paso sales tax rate: 8.25% (6.25% state + 2% local)',
        'File monthly, quarterly, or annually based on sales volume',
        'Must collect and remit sales tax to state',
        'Penalties for late filing or non-compliance',
      ],
    },
    {
      title: 'Texas Franchise Tax',
      authority: 'Texas Comptroller of Public Accounts',
      icon: 'ri-building-line',
      required: 'Required for most business entities (LLCs, corporations, partnerships)',
      cost: 'Based on revenue (no tax if under $2.47M annually)',
      howToApply: 'Register when filing formation documents',
      deadline: 'Annual report due May 15th',
      details: [
        'Calculated on taxable margin (revenue minus deductions)',
        'Rate: 0.375% for most businesses, 0.75% for wholesalers/retailers',
        'Small businesses under threshold pay $0',
        'File annual franchise tax report even if no tax owed',
      ],
    },
    {
      title: 'Federal Income Tax',
      authority: 'Internal Revenue Service (IRS)',
      icon: 'ri-file-text-line',
      required: 'All businesses must file federal income tax returns',
      cost: 'Varies based on business structure and income',
      howToApply: 'File annually using appropriate tax forms',
      deadline: 'March 15 (partnerships/S-corps) or April 15 (sole props/C-corps)',
      details: [
        'Sole proprietors: Schedule C with Form 1040',
        'Partnerships: Form 1065',
        'S Corporations: Form 1120-S',
        'C Corporations: Form 1120',
      ],
    },
    {
      title: 'Employment Taxes',
      authority: 'IRS & Texas Workforce Commission',
      icon: 'ri-team-line',
      required: 'Required if you have employees',
      cost: 'Varies based on payroll',
      howToApply: 'Register with IRS and TWC',
      deadline: 'Before hiring first employee',
      details: [
        'Federal: Social Security, Medicare, unemployment tax',
        'Texas: Unemployment insurance tax',
        'Withhold income tax from employee paychecks',
        'File quarterly payroll tax returns',
      ],
    },
    {
      title: 'El Paso Property Tax',
      authority: 'El Paso Central Appraisal District',
      icon: 'ri-home-line',
      required: 'Required if you own business property or equipment',
      cost: 'Based on property value',
      howToApply: 'File business personal property rendition',
      deadline: 'April 15th annually',
      details: [
        'Applies to real estate, equipment, inventory, furniture',
        'Must file rendition listing all business property',
        'Exemptions available for certain property types',
        'Failure to file can result in 10% penalty',
      ],
    },
  ];

  const registrationSteps = [
    {
      step: '1',
      title: 'Obtain Your EIN',
      description: 'Start by getting your federal Employer Identification Number from the IRS. This is free and can be done online in minutes at IRS.gov/EIN.',
      icon: 'ri-number-1',
    },
    {
      step: '2',
      title: 'Register Business Structure',
      description: 'File your business formation documents with the Texas Secretary of State (if LLC, corporation, or partnership). This automatically registers you for franchise tax.',
      icon: 'ri-number-2',
    },
    {
      step: '3',
      title: 'Apply for Sales Tax Permit',
      description: 'If selling goods or taxable services, register for a sales tax permit through the Texas Comptroller website. You\'ll receive your permit number immediately.',
      icon: 'ri-number-3',
    },
    {
      step: '4',
      title: 'Register for Employment Taxes',
      description: 'If hiring employees, register with the IRS for federal employment taxes and with Texas Workforce Commission for state unemployment insurance.',
      icon: 'ri-number-4',
    },
    {
      step: '5',
      title: 'File Property Rendition',
      description: 'Submit business personal property rendition to El Paso Central Appraisal District by April 15th listing all business assets.',
      icon: 'ri-number-5',
    },
    {
      step: '6',
      title: 'Set Up Tax Calendar',
      description: 'Create a calendar with all tax filing deadlines (monthly sales tax, quarterly payroll, annual income tax, etc.) to avoid penalties.',
      icon: 'ri-number-6',
    },
  ];

  const deadlines = [
    { frequency: 'Monthly', tax: 'Sales Tax', due: '20th of following month', applies: 'High-volume sellers' },
    { frequency: 'Quarterly', tax: 'Sales Tax', due: '20th of month after quarter ends', applies: 'Most businesses' },
    { frequency: 'Quarterly', tax: 'Payroll Taxes', due: 'Last day of month after quarter ends', applies: 'Employers' },
    { frequency: 'Annually', tax: 'Franchise Tax', due: 'May 15th', applies: 'LLCs, corporations, partnerships' },
    { frequency: 'Annually', tax: 'Property Rendition', due: 'April 15th', applies: 'Business property owners' },
    { frequency: 'Annually', tax: 'Federal Income Tax', due: 'March 15 or April 15', applies: 'All businesses' },
  ];

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://elpasotexasbusiness.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Resources",
        "item": "https://elpasotexasbusiness.com/resources"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Tax Registration Guide",
        "item": "https://elpasotexasbusiness.com/resources/tax-registration-guide"
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Tax Registration Guide for El Paso Businesses"
        description="Step-by-step guide to registering your El Paso business for taxes. Get your EIN, Texas sales tax permit, and understand federal, state, and local tax obligations for Texas businesses."
        canonical="https://elpasotexasbusiness.com/resources/tax-registration-guide"
        ogImage="https://public.readdy.ai/ai/img_res/44fd3db9e636d222b6801e325a6094df.jpg"
      />
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        
        <main className="flex-grow">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6">
              <div className="mb-8">
                <Link to="/resources" className="text-[#D2691E] hover:text-[#B8571A] font-semibold text-sm flex items-center space-x-2 whitespace-nowrap">
                  <i className="ri-arrow-left-line"></i>
                  <span>Back to Resources</span>
                </Link>
              </div>
            </div>
          </nav>

          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-16">
              <h1 className="text-6xl font-bold text-gray-900 mb-6">Tax Registration Guide</h1>
              <p className="text-xl text-gray-600 leading-relaxed max-w-4xl">
                Navigate federal, state, and local tax requirements for your El Paso business. This comprehensive guide covers all tax registrations, filing requirements, and important deadlines.
              </p>
            </div>

            <div className="mb-20">
              <div className="bg-[#FFF4E6] border-l-4 border-[#D2691E] rounded-lg p-6 mb-12">
                <div className="flex items-start space-x-4">
                  <i className="ri-information-line text-2xl text-[#D2691E] flex-shrink-0 mt-1"></i>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Important Note</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Tax requirements vary based on your business structure, industry, and activities. This guide provides general information. Consult with a qualified tax professional or CPA for advice specific to your situation.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-4xl font-bold text-gray-900 mb-12">Required Tax Registrations</h2>
              <div className="space-y-8">
                {taxTypes.map((tax, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="flex items-start space-x-6 mb-6">
                      <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center flex-shrink-0">
                        <i className={`${tax.icon} text-3xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{tax.title}</h3>
                        <p className="text-sm text-[#D2691E] font-semibold mb-3">{tax.authority}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Who Needs This</p>
                        <p className="text-sm text-gray-700 font-semibold">{tax.required}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Cost</p>
                        <p className="text-sm text-gray-700 font-semibold">{tax.cost}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">How to Apply</p>
                        <p className="text-sm text-gray-700 font-semibold">{tax.howToApply}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Deadline</p>
                        <p className="text-sm text-gray-700 font-semibold">{tax.deadline}</p>
                      </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-base font-bold text-gray-900 mb-3">Key Details</h4>
                      <ul className="space-y-2">
                        {tax.details.map((detail, detailIndex) => (
                          <li key={detailIndex} className="flex items-start space-x-3">
                            <i className="ri-checkbox-circle-line text-[#D2691E] mt-0.5 flex-shrink-0"></i>
                            <span className="text-sm text-gray-700">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Step-by-Step Registration Process</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {registrationSteps.map((step, index) => (
                  <div key={index} className="bg-[#FAFAF8] rounded-2xl p-8">
                    <div className="w-16 h-16 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                      <span className="text-3xl font-bold text-white">{step.step}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">Tax Filing Calendar</h2>
              <p className="text-base text-gray-600 mb-8 leading-relaxed">
                Stay compliant by knowing when each tax filing is due. Missing deadlines can result in penalties and interest charges.
              </p>
              <div className="bg-[#FAFAF8] rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#D2691E]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Frequency</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Tax Type</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Due Date</th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-white">Applies To</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {deadlines.map((deadline, index) => (
                        <tr key={index} className="hover:bg-white transition-colors">
                          <td className="px-6 py-4 text-sm text-gray-900 font-semibold">{deadline.frequency}</td>
                          <td className="px-6 py-4 text-sm text-gray-900">{deadline.tax}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{deadline.due}</td>
                          <td className="px-6 py-4 text-sm text-gray-600">{deadline.applies}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-12">El Paso Tax Considerations</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-[#FAFAF8] rounded-2xl p-8">
                  <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                    <i className="ri-map-pin-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Local Tax Rates</h3>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li className="flex justify-between"><span>Combined Sales Tax:</span><strong>8.25%</strong></li>
                    <li className="flex justify-between"><span>State Portion:</span><strong>6.25%</strong></li>
                    <li className="flex justify-between"><span>Local Portion:</span><strong>2.00%</strong></li>
                    <li className="flex justify-between"><span>Property Tax Rate:</span><strong>~2.5% of value</strong></li>
                  </ul>
                </div>

                <div className="bg-[#FAFAF8] rounded-2xl p-8">
                  <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                    <i className="ri-gift-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Tax Incentives</h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-4">
                    El Paso offers various tax incentives for new and expanding businesses:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-[#D2691E] mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">Property tax abatements</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-[#D2691E] mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">Foreign Trade Zone benefits</span>
                    </li>
                    <li className="flex items-start space-x-2">
                      <i className="ri-check-line text-[#D2691E] mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-700">Enterprise Zone Program</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-[#FAFAF8] rounded-2xl p-8">
                  <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                    <i className="ri-global-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Border Business Taxes</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    If conducting cross-border business with Mexico, be aware of additional tax considerations including customs duties, import/export taxes, and international tax treaties.
                  </p>
                </div>

                <div className="bg-[#FAFAF8] rounded-2xl p-8">
                  <div className="w-14 h-14 bg-[#D2691E] rounded-xl flex items-center justify-center mb-6">
                    <i className="ri-calculator-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Record Keeping</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Maintain detailed records of all income, expenses, and tax payments for at least 7 years. Use accounting software to track transactions and generate reports for tax filing.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1B3A3A] rounded-3xl p-12">
              <div className="max-w-3xl mx-auto text-center">
                <h3 className="text-4xl font-bold text-white mb-4">Need Tax Assistance?</h3>
                <p className="text-xl text-white/80 mb-8">
                  Tax compliance can be complex. Consider working with a local El Paso CPA or tax professional to ensure you meet all requirements and take advantage of available deductions.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left mb-8">
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-white mb-2">IRS Small Business Resources</h4>
                    <p className="text-sm text-white/80 mb-2">Free tax workshops and guidance</p>
                    <p className="text-sm text-white font-semibold">IRS.gov/SmallBusiness</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-6">
                    <h4 className="text-lg font-bold text-white mb-2">Texas Comptroller</h4>
                    <p className="text-sm text-white/80 mb-2">State tax information and filing</p>
                    <p className="text-sm text-white font-semibold">Comptroller.Texas.gov</p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-3 bg-[#D2691E] text-white px-8 py-4 rounded-full font-semibold hover:bg-[#B8571A] transition-all shadow-lg whitespace-nowrap"
                >
                  <span>Get Personalized Tax Guidance</span>
                  <i className="ri-arrow-right-line text-xl"></i>
                </Link>
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}